import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CommitStrategy, RealtimeConnection, RealtimeEvents, useScribe } from "@elevenlabs/react";
import { supabase } from "@/lib/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SpeechToTextContext, SpeechToTextContextValue, SpeechToTextStatus } from "./context";

// ElevenLabs single-use tokens are valid for ~15 minutes; refresh the idle pre-fetched
// one a bit early so it never gets handed out close to expiring.
const TOKEN_TTL_MS = 15 * 60 * 1000;
const TOKEN_REFRESH_MARGIN_MS = 2 * 60 * 1000;

// Auto-stop a recording session after this long, so nobody accidentally leaves the mic
// running (and burning tokens/connection time) indefinitely.
const SESSION_LIMIT_MS = 2 * 60 * 1000;
// How long the "you were cut off, press the mic to keep going" notice stays visible before
// dismissing itself.
const SESSION_LIMIT_NOTICE_MS = 8000;

const isPermissionError = (err: unknown) =>
    err instanceof DOMException && ["NotAllowedError", "PermissionDeniedError", "SecurityError"].includes(err.name);

type ActiveConnection = {
    connection: RealtimeConnection;
    // Appends whatever's in the latest (not-yet-committed-by-VAD) partial transcript before
    // actually closing, so speech spoken right up until the mic is stopped/switched isn't
    // silently dropped. Purely local/synchronous — no server round-trip needed, unlike trying
    // to force a commit() and wait for the resulting COMMITTED_TRANSCRIPT.
    closeAndFlush: () => void;
};

// Only one field can record at a time, and ElevenLabs tokens are single-use — redeeming one
// invalidates it — so both the scribe connection and token pre-fetching live here, shared by
// every TaskTextarea, instead of duplicated per field.
export function SpeechToTextProvider({ children }: { children: ReactNode }) {
    const { toast } = useToast();

    const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
    const [status, setStatus] = useState<SpeechToTextStatus>("idle");
    const activeFieldIdRef = useRef<string | null>(null);

    // Monotonic counter identifying the current "start recording" attempt. Every listener
    // attached to a connection (see start() below) captures the attemptId it was created for
    // and compares it against this on every event — so a superseded connection's listeners
    // can tell they're stale and self-close, WITHOUT relying on any single shared "current
    // connection" pointer to still correctly point at them. Using useScribe's hook-level
    // callbacks (onCommittedTranscript etc.) instead of this ran into exactly that problem:
    // those callbacks are shared across every connection ever opened on this hook instance,
    // routed through mutable refs, so if a connection ever became orphaned (kept running
    // without us tracking it — e.g. after the SDK's own `if (connectionRef.current) return`
    // guard silently no-ops a connect() call, which doesn't reject or throw), its events would
    // still fire and get routed to whatever field is CURRENTLY considered active, regardless
    // of which field the orphaned connection actually belongs to.
    const connectAttemptRef = useRef(0);

    // Our own record of the currently active connection, rather than relying on
    // scribe.getConnection() for this. The SDK's internal connectionRef isn't safe to treat
    // as ground truth either: its own CLOSE listener sets it to null unconditionally whenever
    // ANY connection closes, not only when connectionRef.current still actually points at
    // that connection — so a stale, already-superseded connection finally closing (its close
    // handshake is async and can complete well after a newer connection has taken over) can
    // wipe out the reference to a connection that's still genuinely live.
    const currentConnectionRef = useRef<ActiveConnection | null>(null);

    // Live preview of whichever field is currently active — see SpeechToTextContextValue.
    const [partialText, setPartialText] = useState("");

    // Which field just got auto-stopped for hitting SESSION_LIMIT_MS, if any — see
    // SpeechToTextContextValue. Tracked separately from activeFieldId/status since by the time
    // this is set, the field is no longer active.
    const [sessionLimitFieldId, setSessionLimitFieldId] = useState<string | null>(null);
    const sessionLimitNoticeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const dismissSessionLimitNotice = useCallback(() => {
        clearTimeout(sessionLimitNoticeTimeoutRef.current);
        setSessionLimitFieldId(null);
    }, []);

    const reset = useCallback(() => {
        activeFieldIdRef.current = null;
        setActiveFieldId(null);
        setStatus("idle");
        setPartialText("");
    }, []);

    // No hook-level callbacks — everything attempt-specific is wired up per-connection in
    // start() instead, scoped to that exact connection object.
    const scribe = useScribe({
        modelId: "scribe_v2_realtime",
        languageCode: "sv",
        enableLogging: false,
        commitStrategy: CommitStrategy.VAD,
    });

    // Closes whatever we currently consider the active connection (using our own tracking,
    // not the SDK's — see currentConnectionRef above), flushing any in-progress transcript
    // first via closeAndFlush. Also calls scribe.disconnect() to synchronously clear the SDK's
    // internal connectionRef regardless of what it currently holds — that matters because
    // close() called directly on a connection object does NOT synchronously clear connectionRef
    // (only the connection's own async close event does, later), so without this the SDK's
    // "already connected" guard inside connect() could silently no-op a subsequent
    // scribe.connect() call while the old socket is still in the process of closing.
    // Entirely synchronous (no network round-trip), so callers don't need to await it.
    const closeActiveConnection = useCallback(() => {
        const active = currentConnectionRef.current;
        currentConnectionRef.current = null;
        active?.closeAndFlush();
        scribe.disconnect();
    }, [scribe]);

    // "unknown" covers both "never checked" and "Permissions API unsupported" (e.g. Safari) —
    // in both cases we can't trust the state without probing via getUserMedia.
    const [micPermissionState, setMicPermissionState] = useState<PermissionState | "unknown">("unknown");
    const micPermissionDenied = micPermissionState === "denied";

    // Proactively reflect the browser's mic permission state where supported (not Safari),
    // so fields can show MicOff before a recording is ever attempted, and so a "granted"
    // state lets start() skip its own getUserMedia probe.
    useEffect(() => {
        if (!navigator.permissions?.query) return;

        let permissionStatus: PermissionStatus | undefined;
        let cancelled = false;

        (async () => {
            try {
                permissionStatus = await navigator.permissions.query({ name: "microphone" as PermissionName });
                if (cancelled) return;
                setMicPermissionState(permissionStatus.state);
                permissionStatus.onchange = () => {
                    setMicPermissionState(permissionStatus?.state ?? "unknown");
                };
            } catch {
                // Permissions API for microphone isn't supported here — denial is
                // instead detected below when a recording is attempted.
            }
        })();

        return () => {
            cancelled = true;
            if (permissionStatus) permissionStatus.onchange = null;
        };
    }, []);

    const fetchToken = useCallback(async (): Promise<string | null> => {
        const { data, error } = await supabase.functions.invoke("get-elevenlabs-token");
        if (error) {
            console.error(error);
            return null;
        }
        return (data as { token: string }).token;
    }, []);

    // Keep exactly one token pre-fetched and ready: start() consumes it instantly (no
    // round-trip wait), then this refills it in the background — either right after it's
    // consumed, or periodically if the mic goes untouched long enough for the idle token
    // to approach its ~15 minute validity window.
    const prefetchedTokenRef = useRef<string | null>(null);
    const tokenRefreshActiveRef = useRef(false);
    const tokenRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const refillPrefetchedToken = useCallback(() => {
        clearTimeout(tokenRefreshTimeoutRef.current);
        fetchToken().then((token) => {
            if (!tokenRefreshActiveRef.current) return;
            prefetchedTokenRef.current = token;
            tokenRefreshTimeoutRef.current = setTimeout(refillPrefetchedToken, TOKEN_TTL_MS - TOKEN_REFRESH_MARGIN_MS);
        });
    }, [fetchToken]);

    useEffect(() => {
        // No point pre-fetching a token nothing can currently use — and this is the only
        // permission transition we actually need to react to (unknown/prompt/granted all
        // behave the same here), so this won't re-run on every intermediate permission value.
        if (micPermissionDenied) return;

        tokenRefreshActiveRef.current = true;
        refillPrefetchedToken();
        return () => {
            tokenRefreshActiveRef.current = false;
            clearTimeout(tokenRefreshTimeoutRef.current);
        };
    }, [micPermissionDenied, refillPrefetchedToken]);

    const stop = useCallback((fieldId: string) => {
        if (activeFieldIdRef.current !== fieldId) return;
        connectAttemptRef.current++;
        closeActiveConnection();
        reset();
    }, [closeActiveConnection, reset]);

    const start = useCallback(async (fieldId: string, onCommittedText: (text: string) => void) => {
        const attemptId = ++connectAttemptRef.current;
        const isCancelled = () => connectAttemptRef.current !== attemptId;

        // Always close whatever's currently connected first (flushing its in-progress
        // transcript — see closeAndFlush) — checked against our own tracking rather than
        // activeFieldIdRef, so a desync between the two can never silently skip this
        // (scribe.connect() below would then just no-op against the still-open old connection
        // instead of erroring). Synchronous, so there's no race to worry about here.
        closeActiveConnection();
        // A fresh start dismisses any stale "you were cut off" notice, whether for this field
        // or another one.
        dismissSessionLimitNotice();

        activeFieldIdRef.current = fieldId;
        setActiveFieldId(fieldId);
        setStatus("connecting");
        // The old field's connection (if any) already flushed its own partial into its own
        // value above — this clears the shared live-preview so the new field doesn't briefly
        // show the previous field's last partial text while connecting.
        setPartialText("");

        // The Permissions API already confirmed access is granted — skip the redundant
        // getUserMedia probe, which adds a noticeable delay. Otherwise probe first, since
        // scribe.connect() itself can't reliably surface a clean "permission denied" signal
        // (its internal getUserMedia errors are swallowed and only reach onError as a string).
        if (micPermissionState !== "granted") {
            let micStream: MediaStream;
            try {
                micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            } catch (err) {
                if (isCancelled()) return;
                reset();
                if (isPermissionError(err)) {
                    setMicPermissionState("denied");
                } else {
                    console.error(err);
                    toast({
                        title: "Talinspelningen kunde inte startas",
                        description: "Vänligen försök igen senare",
                        variant: "destructive",
                    });
                }
                return;
            }
            // Only used to detect permission ahead of scribe.connect(), which opens its own stream.
            micStream.getTracks().forEach((track) => track.stop());
            if (isCancelled()) return;
        }

        let token = prefetchedTokenRef.current;
        prefetchedTokenRef.current = null;
        if (token) {
            // Refill in the background so the next recording doesn't have to wait either.
            refillPrefetchedToken();
        } else {
            // Prefetch hasn't completed (or failed) yet — fall back to fetching on demand.
            token = await fetchToken();
            if (isCancelled()) return;
            if (!token) {
                reset();
                toast({
                    title: "Talinspelningen kunde inte startas",
                    description: "Vänligen försök igen senare",
                    variant: "destructive",
                });
                return;
            }
        }

        try {
            await scribe.connect({
                token,
                microphone: {
                    echoCancellation: true,
                    noiseSuppression: true,
                },
            });
        } catch (err) {
            if (isCancelled()) return;
            reset();
            console.error(err);
            if (isPermissionError(err)) {
                setMicPermissionState("denied");
            } else {
                toast({
                    title: "Talinspelningen kunde inte startas",
                    description: "Vänligen försök igen senare",
                    variant: "destructive",
                });
            }
            return;
        }

        const connection = scribe.getConnection();
        if (!connection) {
            // Shouldn't happen — connect() only throws before a connection is created.
            if (!isCancelled()) reset();
            return;
        }
        if (isCancelled()) {
            connection.close();
            return;
        }

        // Tracks the latest not-yet-committed partial transcript, so it can still be appended
        // if we close before VAD gets around to committing it on its own.
        let pendingPartialText = "";
        // Set once actually recording (see handleSessionStarted) and cleared once this
        // connection closes for any reason (see handleClose).
        let sessionLimitTimeoutId: ReturnType<typeof setTimeout> | undefined;

        const closeAndFlush = () => {
            if (pendingPartialText) {
                const text = pendingPartialText;
                pendingPartialText = "";
                onCommittedText(text);
            }
            connection.close();
        };

        // Clears our own tracking, but only if it's still pointing at this exact connection —
        // a newer start() could otherwise have already replaced it with its own by the time
        // this fires.
        const forgetThisConnection = () => {
            if (currentConnectionRef.current?.connection === connection) currentConnectionRef.current = null;
        };

        currentConnectionRef.current = { connection, closeAndFlush };

        // From here on, everything is wired directly to this connection object instead of
        // going through shared hook-level callbacks — see the comment on connectAttemptRef.
        // The isCancelled() branches below are a backstop for a connection that's been
        // superseded but is somehow still alive — they use closeAndFlush() directly rather
        // than closeActiveConnection(), since currentConnectionRef has already moved on by the
        // time isCancelled() is true here, so this connection isn't what it tracks.
        const handlePartialTranscript = (data: { text: string }) => {
            pendingPartialText = data.text ?? "";
            // Stale connections don't own the shared live preview anymore — a newer attempt
            // (or nothing) already does.
            if (!isCancelled()) setPartialText(pendingPartialText);
        };
        const handleSessionStarted = () => {
            if (isCancelled()) {
                closeAndFlush();
                return;
            }
            setStatus("recording");
            sessionLimitTimeoutId = setTimeout(() => {
                // isCancelled() would already cover this (stop() also bumps connectAttemptRef),
                // but checking activeFieldIdRef directly matches what stop() itself checks —
                // this session may already have ended some other way by now.
                if (activeFieldIdRef.current !== fieldId) return;
                setSessionLimitFieldId(fieldId);
                clearTimeout(sessionLimitNoticeTimeoutRef.current);
                sessionLimitNoticeTimeoutRef.current = setTimeout(() => setSessionLimitFieldId(null), SESSION_LIMIT_NOTICE_MS);
                stop(fieldId);
            }, SESSION_LIMIT_MS);
        };
        const handleCommittedTranscript = (data: { text: string }) => {
            // This segment is now accounted for through the normal commit — nothing left to
            // flush for it, and the live preview of it is about to be superseded by the real
            // value update below.
            pendingPartialText = "";
            if (isCancelled()) {
                connection.close();
                return;
            }
            setPartialText("");
            if (!data.text) return;
            onCommittedText(data.text);
        };
        const handleError = () => {
            if (isCancelled()) {
                closeAndFlush();
                return;
            }
            toast({
                title: "Talinspelningen misslyckades",
                description: "Vänligen försök igen senare",
                variant: "destructive",
            });
            // Not every error closes the connection on its own (e.g. throttling) — force it
            // closed (after flushing any in-progress transcript) so it never keeps streaming
            // while the UI shows idle.
            reset();
            closeActiveConnection();
        };
        const handleClose = () => {
            clearTimeout(sessionLimitTimeoutId);
            connection.off(RealtimeEvents.PARTIAL_TRANSCRIPT, handlePartialTranscript);
            connection.off(RealtimeEvents.SESSION_STARTED, handleSessionStarted);
            connection.off(RealtimeEvents.COMMITTED_TRANSCRIPT, handleCommittedTranscript);
            connection.off(RealtimeEvents.ERROR, handleError);
            connection.off(RealtimeEvents.CLOSE, handleClose);
            forgetThisConnection();
            if (isCancelled()) return;
            reset();
        };

        connection.on(RealtimeEvents.PARTIAL_TRANSCRIPT, handlePartialTranscript);
        connection.on(RealtimeEvents.SESSION_STARTED, handleSessionStarted);
        connection.on(RealtimeEvents.COMMITTED_TRANSCRIPT, handleCommittedTranscript);
        connection.on(RealtimeEvents.ERROR, handleError);
        connection.on(RealtimeEvents.CLOSE, handleClose);
    }, [micPermissionState, scribe, fetchToken, refillPrefetchedToken, toast, reset, closeActiveConnection, dismissSessionLimitNotice, stop]);

    const value = useMemo<SpeechToTextContextValue>(() => ({
        activeFieldId,
        status,
        micPermissionDenied,
        partialText,
        sessionLimitFieldId,
        start,
        stop,
    }), [activeFieldId, status, micPermissionDenied, partialText, sessionLimitFieldId, start, stop]);

    return <SpeechToTextContext.Provider value={value}>{children}</SpeechToTextContext.Provider>;
}
